import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStructuredData } from '@/hooks/useStructuredData';

const REFUND_SCHEMAS = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Refund Policy — Onetvservices 7-Day Money-Back Guarantee',
    url: 'https://onetvservices.com/refund',
    description: 'Onetvservices Refund Policy — 7-day money-back guarantee for first-time subscribers. No questions asked. Full refund processed within 5-10 business days.',
    dateModified: '2026-04-03',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://onetvservices.com' },
        { '@type': 'ListItem', position: 2, name: 'Refund Policy', item: 'https://onetvservices.com/refund' },
      ],
    },
    publisher: {
      '@type': 'Organization',
      name: 'Onetvservices',
      url: 'https://onetvservices.com',
    },
  },
];

export default function RefundPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useStructuredData(REFUND_SCHEMAS);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-white/10 py-6 px-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-lg" />
            <span className="text-sm">Back to Home</span>
          </Link>
          <div className="w-px h-5 bg-white/20" />
          <Link to="/">
            <img
              src="https://storage.readdy-site.link/project_files/ea087323-c332-4d1a-a768-642201e38806/8e03886a-a466-4209-8fc0-30625673693c_ONETV.png?v=410354e2b00bf17cf48639a5651635ec"
              alt="Onetvservices Logo"
              className="h-12 w-auto object-contain"
            />
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Title */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-1.5 text-green-400 text-sm font-medium mb-6">
            <i className="ri-refund-2-line" />
            Legal Document
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Refund Policy</h1>
          <p className="text-white/50 text-sm">Last updated: April 3, 2026</p>
        </div>

        <div className="space-y-10 text-white/80 leading-relaxed">

          {/* Guarantee Banner */}
          <section>
            <div className="bg-gradient-to-r from-green-500/15 to-green-500/5 border border-green-500/25 rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="w-16 h-16 flex items-center justify-center bg-green-500/20 rounded-2xl flex-shrink-0">
                <i className="ri-shield-check-line text-green-400 text-3xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">7-Day Money-Back Guarantee</h2>
                <p className="text-white/70 text-lg">
                  We stand behind our service. If you are not completely satisfied within the first <strong className="text-white">7 days</strong>, we will refund you in full — no questions asked.
                </p>
              </div>
            </div>
          </section>

          {/* 1 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">1</span>
              Eligibility for a Refund
            </h2>
            <p className="mb-4">You are eligible for a full refund if all of the following conditions are met:</p>
            <ul className="space-y-3">
              {[
                'Your refund request is submitted within 7 days of your subscription activation date.',
                'This is your first subscription with Onetvservices (first-time subscribers only).',
                'Your account has not been suspended for violation of our Terms & Conditions.',
                'You have not abused the free trial or refund policy previously.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-green-400 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">2</span>
              Non-Refundable Situations
            </h2>
            <p className="mb-4">Refunds will <strong className="text-white">not</strong> be issued in the following cases:</p>
            <ul className="space-y-3">
              {[
                'The 7-day guarantee period has expired.',
                'The refund request is for a renewal or second subscription.',
                'The account was terminated due to a violation of our Terms & Conditions.',
                'The service was used extensively and the refund request appears to be in bad faith.',
                'Issues caused by your internet connection, device incompatibility, or third-party software.',
                'Partial refunds for unused subscription time after the 7-day period.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-400 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">3</span>
              How to Request a Refund
            </h2>
            <p className="mb-6">Requesting a refund is simple. Follow these steps:</p>
            <div className="space-y-4">
              {[
                {
                  step: '01',
                  title: 'Contact Our Support Team',
                  desc: 'Reach out to us via WhatsApp, email, or phone within 7 days of activation.',
                  icon: 'ri-customer-service-2-line',
                },
                {
                  step: '02',
                  title: 'Provide Your Account Details',
                  desc: 'Share your registered phone number or email and the reason for your refund request.',
                  icon: 'ri-user-line',
                },
                {
                  step: '03',
                  title: 'Confirmation',
                  desc: 'Our team will review your request and confirm eligibility within 24 hours.',
                  icon: 'ri-check-double-line',
                },
                {
                  step: '04',
                  title: 'Refund Processed',
                  desc: 'Once approved, your refund will be processed within 5–10 business days via the original payment method.',
                  icon: 'ri-bank-card-line',
                },
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-5 bg-white/5 border border-white/10 rounded-xl p-5">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-primary text-xl`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">Step {item.step}</span>
                      <h3 className="font-semibold text-white">{item.title}</h3>
                    </div>
                    <p className="text-sm text-white/60">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">4</span>
              Refund Processing Times
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { method: 'Visa / Mastercard', time: '5–10 business days', icon: 'ri-bank-card-line' },
                { method: 'PayPal', time: '3–5 business days', icon: 'ri-paypal-line' },
                { method: 'Bank Transfer', time: '5–7 business days', icon: 'ri-building-line' },
                { method: 'Wafacash / CashPlus', time: '2–5 business days', icon: 'ri-money-dollar-circle-line' },
                { method: 'Binance / Crypto', time: '1–3 business days', icon: 'ri-bit-coin-line' },
                { method: 'Western Union / Remitly', time: '3–7 business days', icon: 'ri-global-line' },
              ].map((item, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-primary`} />
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">{item.method}</p>
                    <p className="text-white/50 text-xs">{item.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">5</span>
              Chargebacks &amp; Disputes
            </h2>
            <p>
              We strongly encourage you to contact us directly before initiating a chargeback with your bank or payment provider. Chargebacks initiated without prior contact may result in immediate account suspension and may affect your eligibility for future services. We are committed to resolving all disputes fairly and promptly.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">6</span>
              Changes to This Policy
            </h2>
            <p>
              We reserve the right to update this Refund Policy at any time. Changes will be effective immediately upon posting. We recommend reviewing this policy periodically. Continued use of our service after changes are posted constitutes your acceptance of the revised policy.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gradient-to-r from-green-500/10 to-green-500/5 border border-green-500/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-2">Need a Refund?</h2>
            <p className="text-white/70 mb-6">
              Our support team is available 7 days a week from 10am to 12am. Don&apos;t hesitate to reach out — we&apos;re here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://wa.me/12089746525"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-green-500/15 hover:bg-green-500/25 border border-green-500/25 rounded-xl px-5 py-3 transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line text-green-400 text-lg" />
                <span className="text-sm font-medium">WhatsApp — Fastest Response</span>
              </a>
              <a
                href="mailto:onetvservices@gmail.com"
                className="flex items-center gap-3 bg-white/10 hover:bg-white/15 border border-white/10 rounded-xl px-5 py-3 transition-colors cursor-pointer"
              >
                <i className="ri-mail-line text-primary text-lg" />
                <span className="text-sm font-medium">onetvservices@gmail.com</span>
              </a>
              <a
                href="tel:+12089746525"
                className="flex items-center gap-3 bg-white/10 hover:bg-white/15 border border-white/10 rounded-xl px-5 py-3 transition-colors cursor-pointer"
              >
                <i className="ri-phone-line text-primary text-lg" />
                <span className="text-sm font-medium">+1 (208) 974 6525</span>
              </a>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 py-8 px-4 text-center text-white/40 text-sm">
        <p>Onetvservices 2019-2026 © All Rights Reserved</p>
        <div className="flex items-center justify-center gap-4 mt-3 flex-wrap">
          <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">Home</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-white/70 transition-colors cursor-pointer">Terms &amp; Conditions</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-white/70 transition-colors cursor-pointer">Privacy Policy</Link>
          <span>·</span>
          <Link to="/refund" className="text-green-400 cursor-pointer">Refund Policy</Link>
        </div>
      </div>
    </div>
  );
}
